import api from '@/lib/axios'
import type { PaginatedResponse, PaginationQuery } from '@/types/api.types'

// ── Types ──────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string
  email: string
  firstName: string
  lastName: string
  isActive: boolean
  twoFactorEnabled: boolean
  createdAt: string
  roles: string[]
}

export interface AdminRole {
  id: string
  name: string
  description?: string
  isSystem: boolean
  entityId: string
  _count?: { userRoles: number; rolePermissions: number }
}

export interface AdminPermission {
  id: string
  resource: string
  action: string
  description?: string
}

export interface InviteUserDto {
  email: string
  firstName: string
  lastName: string
  roleId: string
  phone?: string
  // Fiche employé optionnelle
  createEmployee?: boolean
  jobTitle?: string
  departmentId?: string
  hireDate?: string
  ipresId?: string
}

export interface CreateRoleDto {
  name: string
  description?: string
}

// ── Helper ──────────────────────────────────────────────────────────────────
const res = <T>(r: any): T => r.data?.data ?? r.data

// ── API ────────────────────────────────────────────────────────────────────

export const adminApi = {
  // ── Utilisateurs ──────────────────────────────────────────────────────────
  getUsers: (params: PaginationQuery) =>
    api.get<PaginatedResponse<AdminUser>>('/users', { params }).then((r) => {
      const raw = r.data?.data ?? r.data
      // Backend paginate() retourne { items, meta } — normaliser en { data, meta }
      if (raw?.items) {
        const m = raw.meta
        return {
          data: raw.items,
          meta: {
            ...m,
            hasPreviousPage: m.hasPreviousPage ?? m.hasPrevPage ?? false,
          },
        }
      }
      return raw
    }),

  inviteUser: (dto: InviteUserDto) => api.post('/users/invite', dto).then(res),

  activateUser: (id: string) => api.patch(`/users/${id}/activate`).then(res),

  deactivateUser: (id: string) => api.patch(`/users/${id}/deactivate`).then(res),

  resetUserPassword: (id: string, newPassword: string) =>
    api.patch(`/users/${id}/reset-password`, { newPassword }).then(res),

  deleteUser: (id: string) => api.delete(`/users/${id}`).then(res),

  getUserSessions: (userId: string) => api.get(`/users/${userId}/sessions`).then(res),

  revokeSession: (userId: string, sessionId: string) =>
    api.delete(`/users/${userId}/sessions/${sessionId}`).then(res),

  // ── Rôles ─────────────────────────────────────────────────────────────────
  getRoles: (params?: PaginationQuery) =>
    api.get<PaginatedResponse<AdminRole>>('/rbac/roles', { params }).then((r) => {
      const raw = r.data?.data ?? r.data
      if (Array.isArray(raw)) return { data: raw, meta: { total: raw.length, page: 1, limit: raw.length, totalPages: 1 } }
      return raw
    }),

  createRole: (dto: CreateRoleDto) => api.post<AdminRole>('/rbac/roles', dto).then(res),

  updateRole: (id: string, dto: Partial<CreateRoleDto>) =>
    api.put<AdminRole>(`/rbac/roles/${id}`, dto).then(res),

  deleteRole: (id: string) => api.delete(`/rbac/roles/${id}`).then(res),

  assignPermissionsToRole: (roleId: string, permissionIds: string[]) =>
    api.post(`/rbac/roles/${roleId}/permissions`, { permissionIds }).then(res),

  removePermissionsFromRole: (roleId: string, permissionIds: string[]) =>
    api
      .delete(`/rbac/roles/${roleId}/permissions`, { data: { permissionIds } })
      .then(res),

  assignRoleToUser: (userId: string, roleId: string) =>
    api.post(`/rbac/users/${userId}/roles`, { roleId }).then(res),

  removeRoleFromUser: (userId: string, roleId: string) =>
    api.delete(`/rbac/users/${userId}/roles/${roleId}`).then(res),

  // ── Permissions ───────────────────────────────────────────────────────────
  getPermissions: (resource?: string) =>
    api.get<AdminPermission[]>('/rbac/permissions', { params: { resource } }).then(res),

  // ── Audit ─────────────────────────────────────────────────────────────────
  getAuditLogs: (params: PaginationQuery & { userId?: string; resource?: string }) =>
    api.get('/audit/logs', { params }).then(res),

  // ── Utilisateurs (détail) ──────────────────────────────────────────────────
  getUserById: (id: string) =>
    api
      .get<AdminUser & { userRoles: any[]; lastLoginAt?: string }>(`/users/${id}`)
      .then(res),

  // ── Rôles (détail) ────────────────────────────────────────────────────────
  getRoleById: (id: string) =>
    api
      .get<AdminRole & { permissions: AdminPermission[] }>(`/rbac/roles/${id}`)
      .then(res),

  // ── Entités ───────────────────────────────────────────────────────────────
  getEntities: () => api.get('/entities').then(res),

  createEntity: (dto: {
    name: string
    ninea: string
    rccm?: string
    email?: string
    phone?: string
    address?: string
    currency?: string
  }) => api.post('/entities', dto).then(res),

  updateEntity: (id: string, dto: Record<string, any>) =>
    api.put(`/entities/${id}`, dto).then(res),

  // ── Configuration ─────────────────────────────────────────────────────────
  getAppConfig: () => api.get('/config').then(res),

  updateAppConfig: (data: Record<string, any>) => api.put('/config', data).then(res),
}
